import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { DateFormatter } from '@repo/toolkit/date'
import { useBreakPoint } from '@repo/toolkit/use-break-point'
import Badge from '@repo/ui/badge'
import { CalendarTwoIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'

import { ApplicationStatusEnum } from '../../../lib/types/enums/enrollment/application-status.enum'
import { Enrollment } from '../../../lib/types/models/enrollment/enrollment'
import {
  EnrollmentDatesInput,
  EnrollmentDetailsInput,
  EnrollmentMainTripDetailsInput,
  EnrollmentProperties,
  EnrollmentSecondaryTripDetailsInput
} from '../../../lib/types/models/enrollment/enrollment.properties'
import CountryFlag from '../../elements/country-flag'

export type EnrollmentInformationCardInput = EnrollmentDetailsInput &
  EnrollmentMainTripDetailsInput &
  EnrollmentSecondaryTripDetailsInput &
  EnrollmentDatesInput &
  Pick<Enrollment, 'enrollmentNumber' | 'status'>

type Props =
  | {
      mode: 'redirection'
      enrollment: EnrollmentInformationCardInput
      onLinkClick?: () => void | Promise<void>
    }
  | {
      mode?: 'default'
      enrollment: EnrollmentInformationCardInput
      onLinkClick?: never
    }

const EnrollmentInformationCard = ({ mode, enrollment, onLinkClick }: Props) => {
  const device = useBreakPoint()
  const isMobile = device === 'mobile'

  const enrollmentMainDetails = EnrollmentProperties.getEnrollmentDetails(enrollment)
  const enrollmentMainTrip = EnrollmentProperties.getEnrollmentMainTripDetails(enrollment)
  const enrollmentSecondaryTrip =
    EnrollmentProperties.getEnrollmentSecondaryTripDetails(enrollment)
  const country = EnrollmentProperties.getEnrollmentCountry(enrollment)
  const enrollmentDates = EnrollmentProperties.getEnrollmentDepartureAndArrivalDates(enrollment)

  const isCancelled =
    enrollment.status === ApplicationStatusEnum.CANCELLED_BEFORE_CONFIRMATION ||
    enrollment.status === ApplicationStatusEnum.CANCELLED_AFTER_CONFIRMATION

  const isRedMode = mode === 'default' && isCancelled
  return (
    <div onClick={mode === 'redirection' ? () => void onLinkClick?.() : undefined}>
      <InnerBox
        className={cn('rounded-lg p-3 bg-white-hover', {
          'transition-transform duration-150 ease-out hover:scale-[1.01] cursor-pointer':
            mode === 'redirection',
          'border-red-light-active bg-red-light': isRedMode
        })}
      >
        <div className="flex gap-3 mobile:flex-col">
          <div className="hidden mobile:flex items-start justify-end">
            <Badge
              size="flexible"
              className="px-2 py-1 hidden mobile:block"
              color={tailwindColors.secondaryRed.light.DEFAULT}
              textColor={tailwindColors.secondaryRed.DEFAULT}
              text={`Réf: ${enrollment.enrollmentNumber}`}
            />
          </div>
          <div className="flex flex-col w-full">
            <div className="flex w-full justify-between items-center">
              <div className="flex items-start  gap-2 mobile:items-start ">
                <CountryFlag possibleCountry={country.value} code={country.value} />
                <Typography
                  variant={isMobile ? 'tag' : 'text'}
                  color={tailwindColors.grey.DEFAULT}
                >
                  {enrollmentMainDetails}
                </Typography>
              </div>
              <Badge
                size="flexible"
                className="px-2 py-1 shrink-0 mobile:hidden"
                color={
                  isRedMode
                    ? tailwindColors.white.DEFAULT
                    : tailwindColors.secondaryRed.light.DEFAULT
                }
                textColor={tailwindColors.secondaryRed.DEFAULT}
                text={`Réf: ${enrollment.enrollmentNumber}`}
              />
            </div>
            <div className="h-1" />
            <div
              className={cn('flex flex-col ', {
                'flex-row items-center gap-2': mode === 'default' && !isMobile
              })}
            >
              <Typography
                variant={isMobile ? 'label-semibold' : 'secondary-body'}
                color={tailwindColors.grey.DEFAULT}
              >
                {`(${enrollmentMainTrip}) ${enrollmentSecondaryTrip ? ` - (${enrollmentSecondaryTrip})` : ''}`}
              </Typography>
              <div className="h-2" />
              <div className="flex items-center gap-2">
                {enrollmentDates.value?.startDate && enrollmentDates.value?.endDate && (
                  <Badge
                    className="h-7"
                    size="flexible"
                    color={
                      isRedMode ? tailwindColors.white.DEFAULT : tailwindColors.white.dark.DEFAULT
                    }
                    borderColor={tailwindColors.grey.light.DEFAULT}
                    textColor={tailwindColors.grey.lighter}
                    iconPosition="start"
                    icon={<CalendarTwoIcon color={tailwindColors.primary.DEFAULT} />}
                    text={`${DateFormatter.DDMMYYYY(enrollmentDates.value?.startDate)} - ${DateFormatter.DDMMYYYY(enrollmentDates.value?.endDate)}`}
                  />
                )}
                {isCancelled && (
                  <Badge
                    variant="danger"
                    color={
                      isRedMode
                        ? tailwindColors.red.light.active
                        : tailwindColors.red.light.DEFAULT
                    }
                    text={mode === 'redirection' ? 'Annulé' : 'Bulletin annulé'}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </InnerBox>
    </div>
  )
}

export default EnrollmentInformationCard
